import dayjs, { Dayjs } from 'dayjs'

import { ShortestLeaveIntervalEnum } from '../../types/leave-policy'
import { ITimeOption } from './types'
import { safeToISOString } from '../../functions/safe-date-format'

interface IGenerateTimeOptionsParamsEnd {
  type: 'end'
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  startTimeValue: Dayjs
  hourFormatString: string
  minuteStep: number
  isHalfDayHoliday: boolean
  workingHoursInDay: number
  today?: Dayjs
}

interface IGenerateTimeOptionsParamsStart {
  type: 'start'
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  hourFormatString: string
  minuteStep: number
  today?: Dayjs
}

export const generateTimeOptions = (params: IGenerateTimeOptionsParamsStart | IGenerateTimeOptionsParamsEnd): ITimeOption[] => {
  const options: ITimeOption[] = []
  const current = params.today || dayjs()
  let minuteStep = params.minuteStep
  let startAt = 0
  if (params.type === 'end') {
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay) {
      const hasFractionalHours = params.workingHoursInDay % 1 !== 0
      minuteStep = hasFractionalHours ? 15 : 60
      startAt = params.startTimeValue.minute()
    }
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour) {
      minuteStep = 60
      startAt = params.startTimeValue.minute()
    }
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour) {
      minuteStep = 30
      const startMinute = params.startTimeValue.minute()
      startAt = startMinute - 30 < 0 ? startMinute : startMinute - 30
    }
    if (params.shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes) {
      minuteStep = 15
      startAt = 0
    }
  }

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = startAt; minute < 60; minute += minuteStep) {
      const time = current.hour(hour).minute(minute).second(0).millisecond(0)
      const isoString = safeToISOString(time)
      options.push({
        value: isoString,
        label: time.format(params.hourFormatString),
        disabled: params.type === 'end' && isDisabledTime({
          startTime: params.startTimeValue,
          hour,
          minute,
          shortestLeaveInterval: params.shortestLeaveInterval,
          isHalfDayHoliday: params.isHalfDayHoliday,
          workingHoursInDay: params.workingHoursInDay,
        }),
        key: isoString,
      })
    }
  }

  return options
}

interface IIsDisabledTimeParams {
  startTime: Dayjs
  hour: number
  minute: number
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  isHalfDayHoliday: boolean
  workingHoursInDay: number
}

/**
 * Determines if a specific time should be disabled in the time picker based on various constraints.
 * The function validates if the selected time is valid according to:
 * - Must be after the start time
 * - Must be within working hours
 * - Must respect half-day holiday restrictions
 * - Must comply with the shortest leave interval policy
 *
 * @param {Object} params - The parameters for time validation
 * @param {Dayjs} params.startTime - The reference start time
 * @param {number} params.hour - The hour to check (0-23)
 * @param {number} params.minute - The minute to check (0-59)
 * @param {ShortestLeaveIntervalEnum} params.shortestLeaveInterval - The minimum interval allowed between times
 * @param {boolean} params.isHalfDayHoliday - Whether it's a half-day holiday
 * @param {number} params.workingHoursInDay - Number of working hours in a day
 *
 * @returns {boolean} True if the time should be disabled, false if it's selectable
 *
 * @example
 * // Check if 14:30 should be disabled for a full day leave
 * isDisabledTime({
 *   startTime: dayjs('2024-03-20T09:00:00'),
 *   hour: 14,
 *   minute: 30,
 *   shortestLeaveInterval: ShortestLeaveIntervalEnum.fullDay,
 *   isHalfDayHoliday: false,
 *   workingHoursInDay: 8
 * }) // Returns true (only end of day is valid for full day leave)
 *
 * @example
 * // Check if 13:00 should be disabled for a half day leave
 * isDisabledTime({
 *   startTime: dayjs('2024-03-20T09:00:00'),
 *   hour: 13,
 *   minute: 0,
 *   shortestLeaveInterval: ShortestLeaveIntervalEnum.halfDay,
 *   isHalfDayHoliday: false,
 *   workingHoursInDay: 8
 * }) // Returns false (13:00 is valid for half day - 4 hours after start)
 */
function isDisabledTime({ startTime, hour, minute, shortestLeaveInterval, isHalfDayHoliday, workingHoursInDay }: IIsDisabledTimeParams) {
  const selectedTime = startTime.hour(hour).minute(minute).second(0).millisecond(0)

  const totalMinutes = Math.round(workingHoursInDay * 60)
  const halfDayMinutes = Math.round(totalMinutes / 2)

  if (!selectedTime.isAfter(startTime)) {
    return true
  }

  if (selectedTime.isAfter(startTime.add(totalMinutes, 'minute'))) {
    return true
  }

  if (isHalfDayHoliday && selectedTime.isAfter(startTime.add(halfDayMinutes, 'minute'))) {
    return true
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.fullDay) {
    const fullDayEnd = startTime.add(totalMinutes, 'minute')
    return !selectedTime.isSame(fullDayEnd)
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay) {
    const fullDayEnd = startTime.add(totalMinutes, 'minute')
    const halfDayEnd = startTime.add(halfDayMinutes, 'minute')
    return !selectedTime.isSame(fullDayEnd) && !selectedTime.isSame(halfDayEnd)
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour) {
    return startTime.minute() !== selectedTime.minute()
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour) {
    return selectedTime.diff(startTime, 'minute') % 30 !== 0
  }

  if (shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes) {
    return selectedTime.diff(startTime, 'minute') % 15 !== 0
  }

  return false
}